import Keycloak from 'keycloak-js'

const useSwaEmulator = process.env.VUE_APP_AUTH_MODE === 'swa-emulator'

const keycloak = new Keycloak({
  url: process.env.VUE_APP_KEYCLOAK_URL || 'https://idp.affolter.net',
  realm: process.env.VUE_APP_KEYCLOAK_REALM || 'Tickets',
  clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID || 'tickets'
})

let initialization: Promise<boolean> | null = null

class AuthenticationService {
  public initialize(): Promise<boolean> {
    if (useSwaEmulator) {
      return Promise.resolve(false)
    }

    if (initialization) {
      return initialization
    }

    initialization = keycloak.init({
      pkceMethod: 'S256',
      checkLoginIframe: false
    }).catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error('Keycloak initialization failed', error)
      return false
    }).finally(() => {
      this.cleanCallbackUrl()
    })
    return initialization
  }

  public async getAccessToken(): Promise<string | null> {
    const authenticated = await this.initialize()
    if (!authenticated) {
      return null
    }

    try {
      await keycloak.updateToken(30)
      return keycloak.token || null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Keycloak token refresh failed', error)
      keycloak.clearToken()
      return null
    }
  }

  public async login(): Promise<void> {
    if (useSwaEmulator) {
      window.location.assign(`/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(this.getRedirectUri())}`)
      return
    }

    await this.initialize()
    await keycloak.login({ redirectUri: this.getRedirectUri() })
  }

  public async logout(): Promise<void> {
    if (useSwaEmulator) {
      window.location.assign(`/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`)
      return
    }

    await this.initialize()
    await keycloak.logout({ redirectUri: window.location.origin })
  }

  private getRedirectUri(): string {
    const url = new URL(window.location.href)
    url.hash = ''
    return url.toString()
  }

  private cleanCallbackUrl(): void {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.hash.substring(1))
    const callbackParams = [
      'code',
      'error',
      'error_description',
      'error_uri',
      'iss',
      'kc_action',
      'kc_action_status',
      'session_state',
      'state'
    ]
    let changed = false

    for (const parameter of callbackParams) {
      if (!params.has(parameter)) continue
      params.delete(parameter)
      changed = true
    }

    if (!changed) {
      return
    }

    const remainingHash = params.toString()
    url.hash = remainingHash ? `#${remainingHash}` : ''
    window.history.replaceState(window.history.state, '', url)
  }
}

export const authenticationService = new AuthenticationService()
